(() => {
  'use strict';

  if (window.__SCARVELA_INDEPENDENT_CHECKOUT_V1__) return;
  window.__SCARVELA_INDEPENDENT_CHECKOUT_V1__ = '2026-08-26.5';

  const nativeFetch = window.fetch.bind(window);
  const gid = (type, id) => `gid://shopify/${type}/${String(id).replace(/^gid:\/\/shopify\/[^/]+\//, '')}`;
  const money = (cents) => Number(cents || 0) / 100;
  let running = false;
  let activeButton = null;
  let preparedCart = null;
  let prepareTimer = 0;

  const productRoot = (button) =>
    button?.closest?.('[data-product-root]') ||
    document.querySelector('[data-product-root]') ||
    document;

  const selectedPurchase = (button) => {
    const root = productRoot(button);
    const product = window.yx_product && typeof window.yx_product === 'object'
      ? window.yx_product
      : {};
    const variantId = String(
      root.querySelector('#gfouk-product-form [data-variant-id]')?.value ||
      button?.dataset?.variantId ||
      ''
    );
    const variant = (product.variants || []).find((item) => String(item.id) === variantId);
    if (!variant) throw new Error('The selected bundle could not be read.');

    const subscription =
      root.querySelector('input[name="subscription_enabled"][data-selling-plan-id]:checked') ||
      root.querySelector('input[name="purchase_type"][value="subscription"][data-selling-plan-id]:checked');
    const sellingPlanId = subscription?.dataset?.sellingPlanId || '';
    const allocation = (variant.selling_plan_allocations || []).find(
      (item) => String(item.selling_plan_id) === String(sellingPlanId)
    );
    const quantity = Math.max(1, Number(root.querySelector('input[name="quantity"]')?.value || 1));
    const rawVariantPrice = Number(variant.price ?? 0);
    const variantPrice = Number.isFinite(rawVariantPrice) && rawVariantPrice >= 0
      ? rawVariantPrice
      : 0;
    const allocationPrice = Number(allocation?.price);
    const allocationCompareAtPrice = Number(allocation?.compare_at_price);
    const finalPrice = allocation && Number.isFinite(allocationPrice) && allocationPrice >= 0
      ? allocationPrice
      : variantPrice;
    const originalPrice = allocation && Number.isFinite(allocationCompareAtPrice) && allocationCompareAtPrice >= 0
      ? Math.max(finalPrice, allocationCompareAtPrice)
      : variantPrice;
    const productId = Number(
      product.id ||
      root.querySelector('.yx-subscription-component[data-product-id]')?.dataset?.productId ||
      0
    );
    const productTitle = product.title || root.querySelector('h1')?.textContent?.trim() || '';
    const variantTitle = variant.public_title || variant.title || '';
    const planName = subscription?.dataset?.sellingPlanName || subscription?.dataset?.deliveryText || '';
    const handle = product.handle || location.pathname.split('/products/')[1]?.split(/[?#/]/)[0] || '';
    const imageElement = root.querySelector('.gfouk-gallery__stage img');
    const image = variant.featured_image?.src || variant.featured_image || product.featured_image || imageElement?.currentSrc || imageElement?.src || '';

    return {
      productId,
      productTitle,
      variantId: Number(variantId),
      variantTitle,
      quantity,
      originalPrice,
      finalPrice,
      image,
      url: `/products/${handle}?variant=${variantId}`,
      grams: Number(variant.grams || variant.weight || 0),
      currency: window.Shopify?.currency?.active || 'USD',
      sellingPlan: allocation && sellingPlanId
        ? {
            id: Number(sellingPlanId),
            name: planName,
            groupId: allocation.selling_plan_group_id || product.selling_plan_groups?.[0]?.id || ''
          }
        : null
    };
  };

  const createIndependentCart = async (purchase) => {
    const response = await nativeFetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        query: `mutation ScarVelaIndependentCart($merchandiseId: ID!, $quantity: Int!, $sellingPlanId: ID) {
          cartCreate(input: { lines: [{
            merchandiseId: $merchandiseId,
            quantity: $quantity,
            sellingPlanId: $sellingPlanId
          }] }) {
            cart {
              id
              totalQuantity
              lines(first: 1) {
                nodes {
                  quantity
                  merchandise { ... on ProductVariant { id } }
                  sellingPlanAllocation { sellingPlan { id } }
                }
              }
            }
            userErrors { field message }
          }
        }`,
        variables: {
          merchandiseId: gid('ProductVariant', purchase.variantId),
          quantity: purchase.quantity,
          sellingPlanId: purchase.sellingPlan ? gid('SellingPlan', purchase.sellingPlan.id) : null
        }
      })
    });
    const json = await response.json().catch(() => ({}));
    const result = json?.data?.cartCreate;
    const errors = result?.userErrors || [];
    const cart = result?.cart;
    const line = cart?.lines?.nodes?.[0];
    const actualVariantId = Number(String(line?.merchandise?.id || '').split('/').pop());
    const actualPlanId = line?.sellingPlanAllocation?.sellingPlan?.id
      ? Number(String(line.sellingPlanAllocation.sellingPlan.id).split('/').pop())
      : null;
    const expectedPlanId = purchase.sellingPlan?.id || null;
    if (
      !response.ok || json.errors?.length || errors.length || !cart?.id ||
      Number(cart.totalQuantity) !== purchase.quantity ||
      Number(line?.quantity) !== purchase.quantity ||
      actualVariantId !== purchase.variantId ||
      actualPlanId !== expectedPlanId
    ) {
      throw new Error(
        errors[0]?.message || json.errors?.[0]?.message ||
        'The independent checkout did not match the selected bundle.'
      );
    }
    return String(cart.id).replace(/^gid:\/\/shopify\/Cart\//, '');
  };

  const purchaseKey = (purchase) => [
    purchase.variantId,
    purchase.quantity,
    purchase.sellingPlan?.id || 0
  ].join(':');

  const prepareCart = (purchase) => {
    const key = purchaseKey(purchase);
    if (preparedCart?.key === key) return preparedCart.promise;
    const promise = createIndependentCart(purchase).catch((error) => {
      if (preparedCart?.promise === promise) preparedCart = null;
      throw error;
    });
    preparedCart = { key, promise };
    return promise;
  };

  const scheduleCartPreparation = () => {
    window.clearTimeout(prepareTimer);
    preparedCart = null;
    prepareTimer = window.setTimeout(() => {
      const button = document.querySelector('#yx_checkout, #yx_paypal_checkout');
      if (!button) return;
      try {
        prepareCart(selectedPurchase(button)).catch(() => {});
      } catch {
        preparedCart = null;
      }
    }, 500);
  };

  const buildCheckoutPayload = (purchase, cartToken) => {
    const originalUnitCents = Math.max(0, Number(purchase.originalPrice) || 0);
    const finalUnitCents = Math.max(0, Number(purchase.finalPrice) || 0);
    const originalPrice = money(originalUnitCents);
    const price = money(finalUnitCents);
    const originalLinePrice = money(originalUnitCents * purchase.quantity);
    const linePrice = money(finalUnitCents * purchase.quantity);
    const totalDiscount = money(Math.max(0, originalUnitCents - finalUnitCents) * purchase.quantity);
    return {
      cart: {
        items: [{
          variantId: gid('ProductVariant', purchase.variantId),
          quantity: purchase.quantity,
          title: purchase.variantTitle
            ? `${purchase.productTitle} - ${purchase.variantTitle}`
            : purchase.productTitle,
          variant: purchase.variantTitle,
          originalPrice,
          price,
          linePrice,
          totalDiscount,
          productId: gid('Product', purchase.productId),
          url: purchase.url,
          image: purchase.image,
          weight: purchase.grams,
          weightUnit: 'GRAMS',
          currencyCode: purchase.currency,
          sellingPlanId: purchase.sellingPlan?.id || null,
          sellingPlanName: purchase.sellingPlan?.name || '',
          sellingPlanGroupId: purchase.sellingPlan?.groupId || ''
        }],
        cartToken,
        originalTotalPrice: originalLinePrice,
        totalPrice: linePrice,
        totalDiscount,
        totalWeight: purchase.grams * purchase.quantity,
        itemCount: purchase.quantity,
        currency: purchase.currency
      },
      position: {
        countryCode: window.Shopify?.country || 'US',
        regionCode: '',
        city: ''
      },
      checkoutId: '',
      language: window.Shopify?.locale || document.documentElement.lang || 'en',
      extra: {
        view: 'default',
        sourceUrl: `${location.origin}${location.pathname}?view=default&scarvelaIndependentCheckout=2026-08-26.5`,
        scarvelaIndependentCheckout: '2026-08-26.5',
        cookie_id: ''
      }
    };
  };

  const shopifyId = () => String(window.Shopify?.shop || '').split('.')[0] || '1eixvf-1j';
  const checkoutApi = async (path, body) => {
    const response = await nativeFetch(`https://us.ouyifyapi.com${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Shopify-Id': shopifyId(),
        oyAppVersion: 'v2'
      },
      body: JSON.stringify(body)
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || (json.code != null && Number(json.code) !== 200)) {
      throw new Error(json.message || `Checkout failed (${response.status}).`);
    }
    return json.data ?? json;
  };

  const resolvePayPalUrl = (payload) => {
    const data = payload?.data ?? payload ?? {};
    const direct = String(data.paypalPaymentUrl || '').trim();
    if (direct && !/\/thanks|\/paypal\/return|\/confirm-address/i.test(direct)) return direct;
    const links = data.paypalOrder?.links;
    if (Array.isArray(links)) {
      for (const rel of ['payer-action', 'approve']) {
        const url = String(links.find((link) => link?.rel === rel)?.href || '').trim();
        if (/paypal\.com/i.test(url)) return url;
      }
    }
    return String(data.redirectUrl || data.paymentUrl || '').trim();
  };

  const addPayPalFunding = (url) => {
    const target = new URL(url, location.origin);
    if (!target.searchParams.has('useraction')) target.searchParams.set('useraction', 'commit');
    if (!target.searchParams.has('fundingSource')) target.searchParams.set('fundingSource', 'paypal');
    return target.href;
  };

  const ensureOverlay = () => {
    if (!document.querySelector('#scarvela-processing-styles')) {
      const style = document.createElement('style');
      style.id = 'scarvela-processing-styles';
      style.textContent = `
        html.scarvela-checkout-processing,
        html.scarvela-checkout-processing body { overflow: hidden !important; }
        .scarvela-processing-overlay {
          position: fixed; inset: 0; z-index: 2147483000; display: grid;
          min-height: 100dvh; place-items: center;
          background: rgba(255,255,255,.92); color: #164f3b;
          backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
        }
        .scarvela-processing-overlay[hidden] { display: none !important; }
        .scarvela-processing-card { display: grid; justify-items: center; gap: 20px; }
        .scarvela-processing-ring {
          width: 112px; height: 112px; border: 5px solid currentColor;
          border-top-color: transparent; border-radius: 50%;
          animation: scarvela-processing-spin 1s linear infinite;
        }
        .scarvela-processing-label {
          color: #111; font: 800 clamp(26px, 5vw, 38px)/1 Arial, Helvetica, sans-serif;
        }
        @keyframes scarvela-processing-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .scarvela-processing-ring { animation-duration: 1.8s; } }
      `;
      document.head.append(style);
    }
    let overlay = document.querySelector('#scarvela-processing-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'scarvela-processing-overlay';
      overlay.className = 'scarvela-processing-overlay';
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'assertive');
      overlay.setAttribute('aria-label', 'Processing secure checkout');
      overlay.innerHTML = '<div class="scarvela-processing-card"><span class="scarvela-processing-ring" aria-hidden="true"></span><div class="scarvela-processing-label">Processing…</div></div>';
      document.body.append(overlay);
    }
    return overlay;
  };

  const setLoading = (button) => {
    activeButton = button;
    button.dataset.scarvelaOriginalHtml = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    document.documentElement.classList.add('scarvela-checkout-processing');
    ensureOverlay().hidden = false;
  };

  const resetLoading = () => {
    if (activeButton) {
      if (activeButton.dataset.scarvelaOriginalHtml) activeButton.innerHTML = activeButton.dataset.scarvelaOriginalHtml;
      delete activeButton.dataset.scarvelaOriginalHtml;
      activeButton.disabled = false;
      activeButton.removeAttribute('aria-busy');
    }
    activeButton = null;
    const overlay = document.querySelector('#scarvela-processing-overlay');
    if (overlay) overlay.hidden = true;
    document.documentElement.classList.remove('scarvela-checkout-processing');
    running = false;
  };

  const startCheckout = async (button) => {
    if (running) return;
    running = true;
    let navigating = false;
    setLoading(button);
    try {
      const purchase = selectedPurchase(button);
      const cartToken = await prepareCart(purchase);
      const payload = buildCheckoutPayload(purchase, cartToken);
      if (button.id === 'yx_paypal_checkout') {
        const created = await checkoutApi('/api/checkout/paypal', payload);
        const paymentUrl = resolvePayPalUrl(created);
        if (!paymentUrl) throw new Error('PayPal payment URL was not returned.');
        navigating = true;
        location.assign(addPayPalFunding(paymentUrl));
        return;
      }
      const checkout = await checkoutApi('/api/checkout', payload);
      if (!checkout.checkoutId) throw new Error('Checkout ID was not returned.');
      navigating = true;
      location.assign(`/a/s/checkout/${checkout.checkoutId}`);
    } catch (error) {
      console.error('[SCARVELA independent checkout]', error);
      alert('Checkout could not be started. Please try again.');
    } finally {
      if (!navigating) resetLoading();
    }
  };

  window.addEventListener('click', (event) => {
    const button = event.target?.closest?.('#yx_checkout, #yx_paypal_checkout');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    startCheckout(button);
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.target?.matches?.('input[name="bundle-choice"], input[name="subscription_enabled"], input[name="purchase_type"], input[name="quantity"]')) return;
    scheduleCartPreparation();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCartPreparation, { once: true });
  } else {
    scheduleCartPreparation();
  }

  window.addEventListener('pagehide', resetLoading);
  window.addEventListener('pageshow', resetLoading);
  document.documentElement.dataset.scarvelaCheckoutHandler = 'independent-selected-bundle-v1';
})();
